import { type FC, type ReactNode, useCallback, useState } from "react";
import { Link } from "react-router-dom";

import { Box, Button, Group, Switch, Text, TextInput, Textarea, Tooltip } from "@mantine/core";
import { useFormik } from "formik";
import { useIntl } from "react-intl";
import { object, string } from "yup";

import { useRemoteAssetsRoutes } from "../../embed/routes/useRemoteAssetsRoutes";
import { DOMAIN } from "../../shared/constants";
import { useDeleteRemoteAssets } from "../hooks/useDeleteRemoteAssets";
import { type SetMutationStatus } from "../hooks/useMutationStatus";
import { useUpdateRemoteAsset } from "../hooks/useUpdateRemoteAsset";
import { formatBytes } from "../utils/formatBytes";

import {
    actions,
    detailLensLink,
    detailMoreButton,
    fieldLabel,
    inUseWarning,
    lensList,
    lensUsagesError,
    metaRow,
    metaSecondary,
} from "./RemoteAssetDetailPage.module.css";
import {
    DeleteInlineConfirm,
    DeleteInlineNo,
    DeleteInlineYes,
    DetailAssetInUse,
    DetailCopying,
    DetailCopyingAllowed,
    DetailCopyingNotAllowed,
    DetailCreated,
    DetailCreatedBy,
    DetailDelete,
    DetailDescription,
    DetailLensUsages,
    DetailName,
    DetailSave,
    DetailSize,
    DetailUpdated,
    DetailUpdatedBy,
    DetailUsedBy,
    LoadingLensUsages,
    ShowMoreLenses,
    ValidationDescriptionMaxLength,
    ValidationNameMaxLength,
    ValidationNameMinLength,
    ValidationNameRequired,
} from "./messages.generated";

const NAME_MIN = 3;
const NAME_MAX = 100;
const DESCRIPTION_MAX = 500;

const validationSchema = object({
    name: string()
        .required("required")
        .test("trimmed-min", "minLength", (value) => value.trim().length >= NAME_MIN)
        .test("trimmed-max", "maxLength", (value) => value.trim().length <= NAME_MAX),
    description: string().max(DESCRIPTION_MAX, "maxLength"),
});

const mapNameError = (error: string | undefined): ReactNode | undefined => {
    switch (error) {
        case "required":
            return <ValidationNameRequired />;
        case "minLength":
            return <ValidationNameMinLength min={NAME_MIN} />;
        case "maxLength":
            return <ValidationNameMaxLength max={NAME_MAX} />;
        default:
            return undefined;
    }
};

const mapDescriptionError = (error: string | undefined): ReactNode | undefined => {
    switch (error) {
        case "maxLength":
            return <ValidationDescriptionMaxLength max={DESCRIPTION_MAX} />;
        default:
            return undefined;
    }
};

interface RemoteAssetDetailFormProps {
    asset: {
        id: string;
        name: string;
        description?: string | null;
        type: string;
        assetSize: number;
        lensUsages: number;
        createdAt: string;
        createdBy: string;
        updatedAt: string;
        updatedBy: string;
        forkingAllowed: boolean;
    };
    lenses: readonly { lensId: string; lensName: string }[];
    hasMoreLenses: boolean;
    onShowMoreLenses: () => void;
    loadingMoreLenses: boolean;
    loadMoreLensesErrorMessage: string | undefined;
    setMutationStatus: SetMutationStatus;
    isSaving: boolean;
    isDeleting: boolean;
    onDeleted: () => void;
}

export const RemoteAssetDetailForm: FC<RemoteAssetDetailFormProps> = ({
    asset,
    lenses,
    hasMoreLenses,
    onShowMoreLenses,
    loadingMoreLenses,
    loadMoreLensesErrorMessage,
    setMutationStatus,
    isSaving,
    isDeleting,
    onDeleted,
}) => {
    const intl = useIntl();
    const routes = useRemoteAssetsRoutes();
    const [confirmingDelete, setConfirmingDelete] = useState(false);

    const formik = useFormik({
        initialValues: {
            name: asset.name,
            description: asset.description ?? "",
            forkingAllowed: asset.forkingAllowed,
        },
        validationSchema,
        onSubmit: async (values) => {
            await updateAsset(asset.id, {
                name: values.name.trim(),
                description: values.description,
                forkingAllowed: values.forkingAllowed,
            });
        },
    });

    const { updateAsset } = useUpdateRemoteAsset(setMutationStatus, {
        onCompleted: (updated) => {
            formik.resetForm({
                values: {
                    name: updated.name,
                    description: updated.description ?? "",
                    forkingAllowed: updated.forkingAllowed,
                },
            });
        },
    });

    const { deleteAssets } = useDeleteRemoteAssets(setMutationStatus);

    const isInUse = asset.lensUsages > 0;

    const handleDelete = useCallback(async () => {
        const success = await deleteAssets([asset.id]);
        if (success) onDeleted();
    }, [asset.id, deleteAssets, onDeleted]);

    const formatFullDate = (dateStr: string): string =>
        intl.formatDate(new Date(dateStr), { month: "short", day: "numeric", year: "numeric" });

    const formatExactDate = (dateStr: string): string =>
        intl.formatDate(new Date(dateStr), {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
        });

    return (
        <form onSubmit={formik.handleSubmit}>
            <Box>
                <Text className={fieldLabel}>
                    <DetailName />
                </Text>
                <TextInput
                    name="name"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.name && mapNameError(formik.errors.name)}
                    maxLength={NAME_MAX}
                />
            </Box>

            <Box mt="md">
                <Text className={fieldLabel}>
                    <DetailDescription />
                </Text>
                <Textarea
                    name="description"
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    minRows={3}
                    error={formik.touched.description && mapDescriptionError(formik.errors.description)}
                    maxLength={DESCRIPTION_MAX}
                />
            </Box>

            <div className={metaRow}>
                <Box>
                    <Text className={fieldLabel}>
                        <DetailSize />
                    </Text>
                    <Text size="sm">{formatBytes(asset.assetSize)}</Text>
                </Box>
                <Box>
                    <Text className={fieldLabel}>
                        <DetailUsedBy />
                    </Text>
                    <Text size="sm">
                        <DetailLensUsages count={asset.lensUsages} />
                    </Text>
                    {(lenses.length > 0 || hasMoreLenses) && (
                        <div className={lensList}>
                            {lenses.map((lens) => {
                                const lensPath = routes.lensDetail(lens.lensId);
                                if (lensPath) {
                                    return (
                                        <Link key={lens.lensId} to={lensPath} className={detailLensLink}>
                                            {lens.lensName}
                                        </Link>
                                    );
                                }
                                return (
                                    <button
                                        key={lens.lensId}
                                        type="button"
                                        className={detailLensLink}
                                        onClick={() => {
                                            routes.openExternal(`https://${DOMAIN}/lens/${lens.lensId}`);
                                        }}
                                    >
                                        {lens.lensName}
                                    </button>
                                );
                            })}
                            {hasMoreLenses && (
                                <button
                                    type="button"
                                    className={detailMoreButton}
                                    onClick={onShowMoreLenses}
                                    disabled={loadingMoreLenses}
                                >
                                    {loadingMoreLenses ? <LoadingLensUsages /> : <ShowMoreLenses />}
                                </button>
                            )}
                        </div>
                    )}
                    {loadMoreLensesErrorMessage && (
                        <Text size="sm" c="red" className={lensUsagesError}>
                            {loadMoreLensesErrorMessage}
                        </Text>
                    )}
                </Box>
            </div>

            <div className={metaRow}>
                <Box>
                    <Text className={fieldLabel}>
                        <DetailCreated />
                    </Text>
                    <Tooltip label={formatExactDate(asset.createdAt)}>
                        <Text size="sm">{formatFullDate(asset.createdAt)}</Text>
                    </Tooltip>
                    {asset.createdBy && (
                        <Text size="xs" className={metaSecondary}>
                            <DetailCreatedBy name={asset.createdBy} />
                        </Text>
                    )}
                </Box>
                <Box>
                    <Text className={fieldLabel}>
                        <DetailUpdated />
                    </Text>
                    <Tooltip label={formatExactDate(asset.updatedAt)}>
                        <Text size="sm">{formatFullDate(asset.updatedAt)}</Text>
                    </Tooltip>
                    {asset.updatedBy && (
                        <Text size="xs" className={metaSecondary}>
                            <DetailUpdatedBy name={asset.updatedBy} />
                        </Text>
                    )}
                </Box>
            </div>

            <Box mt="md">
                <Text className={fieldLabel}>
                    <DetailCopying />
                </Text>
                <Group gap="sm">
                    <Switch
                        checked={formik.values.forkingAllowed}
                        onChange={(e) => {
                            void formik.setFieldValue("forkingAllowed", e.currentTarget.checked);
                        }}
                    />
                    <Text size="sm">
                        {formik.values.forkingAllowed ? <DetailCopyingAllowed /> : <DetailCopyingNotAllowed />}
                    </Text>
                </Group>
            </Box>

            <div className={actions}>
                <Button type="submit" disabled={!formik.dirty || !formik.isValid} loading={isSaving}>
                    <DetailSave />
                </Button>
                {confirmingDelete ? (
                    <Group gap="sm">
                        <Text size="sm">
                            <DeleteInlineConfirm />
                        </Text>
                        <Button
                            variant="filled"
                            color="red"
                            onClick={() => {
                                void handleDelete();
                            }}
                            loading={isDeleting}
                        >
                            <DeleteInlineYes />
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setConfirmingDelete(false);
                            }}
                        >
                            <DeleteInlineNo />
                        </Button>
                    </Group>
                ) : (
                    <Button
                        variant="outline"
                        color="red"
                        onClick={() => {
                            setConfirmingDelete(true);
                        }}
                        disabled={isInUse}
                        loading={isDeleting}
                    >
                        <DetailDelete />
                    </Button>
                )}
            </div>
            {isInUse && (
                <Text className={inUseWarning}>
                    <DetailAssetInUse count={asset.lensUsages} />
                </Text>
            )}
        </form>
    );
};
